<template>
  <section class="v2-field" :class="{'v2-field--editing': editing}" :data-qid="'task-field-' + field">
    <header class="v2-field__head">
      <h4>
        {{ title }}
        <span v-if="required" class="text-red">*</span>
        <font-awesome-icon v-if="report"
                           icon="file-alt"
                           class="v2-field__report"
                           v-b-tooltip.hover
                           :title="$t('tasks.v2.output_to_report')"
        />
      </h4>
      <span v-if="ai && canEdit && getClient.aiModule"
            class="v2-ai-btn"
            :class="{'v2-ai-btn--on': aiOpen}"
            @click="toggleAi"
            :data-qid="'task-field-ai-' + field"
      >
        <font-awesome-icon icon="sparkles"/>{{ $t('tasks.v2.write_with_ai') }}
      </span>
      <span v-if="canEdit && !editing"
            class="v2-field__icon"
            @click="startEdit"
            v-b-tooltip.hover
            :title="$t('system.edit')"
            :data-qid="'task-field-edit-' + field"
      ><font-awesome-icon icon="pen"/></span>
    </header>

    <!-- Above the text, e.g. the response choices of the conclusion -->
    <slot></slot>

    <template v-if="editing">
      <FormattedNoteEditor v-model="taskForm[field]"
                           :rows="5"
                           :placeholder="placeholder"
                           :qid="'task-field-input-' + field"
      ></FormattedNoteEditor>
      <div class="v2-field__actions">
        <span class="v2-field__hint">{{ $t('tasks.v2.save_hint') }}</span>
        <b-button size="sm"
                  variant="secondary"
                  @click="discard"
                  :data-qid="'task-field-discard-' + field">{{ $t('tasks.modal.discard') }}</b-button>
        <b-button size="sm"
                  variant="success"
                  @click="save"
                  :data-qid="'task-field-save-' + field">{{ $t('tasks.modal.save') }}</b-button>
      </div>
    </template>

    <template v-else>
      <div v-if="taskForm[field]" class="v2-field__text text-formatted" :class="{'v2-field__text--clamped': long && !expanded}"><FormattedNoteText :value="taskForm[field]"/></div>
      <div v-else class="v2-field__empty">{{ $t('tasks.v2.empty_field') }}</div>
      <span v-if="long" class="v2-link cursor-pointer" @click="expanded = !expanded">
        {{ expanded ? $t('system.view_less') : $t('system.view_more') }}
      </span>
    </template>
  </section>
</template>

<script>
import assessmentSingleTaskProxy from './assessmentSingleTaskProxy';
import FormattedNoteEditor from '@/components/tasks/FormattedNoteEditor.vue';
import FormattedNoteText from '@/components/tasks/FormattedNoteText.vue';
import { noteToPlainText } from '@/components/tasks/formattedNote';

// One text field of the task (control description, performed test, testing notes, conclusion).
// Each field is saved on its own, with the check for changes by another user (US 9345).
// All of them allow bold, italic and underline (US 9390).
const FIELDS = {
  control_description: { editMode: 'editModeControlDescription', save: 'updateControlDescription', discard: 'discardControlDescription', ai: 'isControlDescriptionDrawerOpen', toggle: 'toggleControlDescriptionDrawer' },
  performed_text: { editMode: 'editModePerformedTest', save: 'updatePerformedTest', discard: 'discardPerformedTest', ai: 'isTestingDrawerOpen', toggle: 'toggleTestingDrawer' },
  testing_notes: { editMode: 'editModeTestingNotes', save: 'updateTestingNotes', discard: 'discardTestingNotes' },
  remarks: { editMode: 'editModeConclusion', save: 'updateConclusion', discard: 'discardConclusion' },
  notes: { editMode: 'editModeNotes', save: 'updateNotes', discard: 'discardNotes' },
  data_remarks: { editMode: 'editModeControlDescriptionDocumentationCollection', save: 'updateDataRemarks', discard: 'discardDataRemarks' }
};

export default {
  name: 'EngagementTaskTextCard',
  mixins: [assessmentSingleTaskProxy],
  components: {
    FormattedNoteEditor,
    FormattedNoteText
  },
  props: {
    field: {
      type: String,
      required: true
    },
    title: {
      type: String,
      required: true
    },
    placeholder: {
      type: String,
      default: ''
    },
    required: {
      type: Boolean,
      default: false
    },
    // Shown in the report
    report: {
      type: Boolean,
      default: false
    },
    // Has a Write with AI button
    ai: {
      type: Boolean,
      default: false
    },
    // Read-only, e.g. the client's comment during OE testing
    locked: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      expanded: false
    };
  },
  computed: {
    config() {
      return FIELDS[this.field];
    },
    editing() {
      return this.canEdit && !!this[this.config.editMode];
    },
    canEdit() {
      return this.selectedTask && !this.readOnly && !this.locked;
    },
    long() {
      return noteToPlainText(this.taskForm[this.field]).length > 420;
    },
    aiOpen() {
      return !!(this.config.ai && this[this.config.ai]);
    }
  },
  methods: {
    startEdit() {
      this[this.config.editMode] = true;
    },
    save() {
      this[this.config.save](this.selectedTask);
    },
    discard() {
      this[this.config.discard](this.selectedTask);
    },
    toggleAi() {
      this[this.config.toggle]();
    }
  }
};
</script>
